import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Respiratory subsection adapter
 * Handles respiratory assessment, cough, oxygen, and lung sounds (5 fields)
 */
export class RespiratoryAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Respiratory assessment fields
    const respiratoryStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL
    )?.at(0)

    const cough = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH
    )?.at(0)

    const oxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN
    )?.at(0)

    const lungSounds = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS
    )

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [DischargeFormField.RESPIRATORY_STATUS]: respiratoryStatus,
      [DischargeFormField.RESPIRATORY_COUGH]: cough,
      [DischargeFormField.RESPIRATORY_OXYGEN]: oxygen,
      [DischargeFormField.RESPIRATORY_LUNG_SOUNDS]: lungSounds,
      [DischargeFormField.RESPIRATORY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.RESPIRATORY_STATUS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL,
        value: this.getFormDataField(DischargeFormField.RESPIRATORY_STATUS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RESPIRATORY_COUGH)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH,
        value: this.getFormDataField(DischargeFormField.RESPIRATORY_COUGH)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RESPIRATORY_OXYGEN)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN,
        value: this.getFormDataField(DischargeFormField.RESPIRATORY_OXYGEN)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RESPIRATORY_LUNG_SOUNDS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS,
        value: this.getFormDataField(DischargeFormField.RESPIRATORY_LUNG_SOUNDS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RESPIRATORY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY,
        value: this.getFormDataField(DischargeFormField.RESPIRATORY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
